import { useEffect, useContext } from "react";
import { RouteComponentProps } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import URI from "urijs";
import { AppContext } from "../app/store";
import { ActionType, UserLanguage, AppState } from "../@types";
import { isOutsideOpenEnrollment, parseSearchParams } from ".";

export const useQueryParams = ({
  history,
  location,
  match,
}: RouteComponentProps): {
  state: AppState;
  routeProps: RouteComponentProps;
} => {
  const { state, dispatch } = useContext(AppContext);
  const {
    mctCurrentPlanYear,
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();
  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const isValidYear = (year = ""): boolean => {
    if (mctYpOeNextYear) {
      return year === (mctCurrentPlanYear + 1).toString();
    }
    if (!outsideOpenEnrollment) {
      return [
        mctCurrentPlanYear.toString(),
        (mctCurrentPlanYear + 1).toString(),
      ].includes(year);
    } else {
      return year === mctCurrentPlanYear.toString();
    }
  };

  const updateUrl = (uri: URI, year: string, lang: UserLanguage): void => {
    uri.setQuery("year", year);
    if (lang && [UserLanguage.ENGLISH, UserLanguage.SPANISH].includes(lang)) {
      uri.setQuery("lang", lang);
    }
    location.search = uri.search();
    history.replace(location);
  };

  const processQueryParams = (location: { search: string }): void => {
    const uri = new URI(location.search);
    const { year, lang } = parseSearchParams(uri.search(true));

    const defaultYear =
      !outsideOpenEnrollment || mctYpOeNextYear
        ? (mctCurrentPlanYear + 1).toString()
        : mctCurrentPlanYear.toString();
    if (!isValidYear(year) && state.year && isValidYear(state.year)) {
      updateUrl(uri, state.year, lang as UserLanguage);
    } else if (state.year !== year && isValidYear(year)) {
      dispatch({ type: ActionType.UPDATE_YEAR, payload: year });
      updateUrl(uri, year, lang as UserLanguage);
    } else if (!state.year || !isValidYear(year)) {
      dispatch({ type: ActionType.UPDATE_YEAR, payload: defaultYear });
      updateUrl(uri, defaultYear, lang as UserLanguage);
    }
  };

  const handleAppLanguage = (location: { search: string }): void => {
    const uri = new URI(location.search);
    const { lang: languageInUrl } = parseSearchParams(uri.search(true));
    const { language: currentLanguage } = state;

    if (
      [UserLanguage.ENGLISH, UserLanguage.SPANISH].includes(
        languageInUrl as UserLanguage
      )
    ) {
      if (currentLanguage !== languageInUrl) {
        dispatch({ type: ActionType.UPDATE_LANGUAGE, payload: languageInUrl });
      }
    }

    if (!languageInUrl) {
      uri.setQuery("lang", currentLanguage);
      location.search = uri.search();
      history.replace(location);
    }
  };

  useEffect(() => {
    if (mctYpOpenEnrollment !== undefined || mctYpPublicPreview !== undefined) {
      processQueryParams(location);
    }

    handleAppLanguage(location);
  }, [location.search, mctYpOpenEnrollment, mctYpPublicPreview]);

  return { state: state, routeProps: { history, location, match } };
};
